import { useGetAllNotificationJobs, } from "services/ContentManager";
import { isAdmin as getIsAdmin } from "services/PermissionManager";
import { Grid, Alert, Box, Divider, Typography} from "@mui/material";
import { AddCircle, } from '@mui/icons-material'
import React from "react";
import { UserContext } from "containers/App";
import { useTranslation } from "react-i18next";
import { deleteElem, get, MESSAGES, post } from "services/Client";
import {useQueryClient} from "react-query";
import StartIconButton from "components/buttons/StartIconButton";
import StartNotificationJobModal from "./StartNotificationJobModal";
import { NotificationJobCardDetail } from "./NotificationJobCardDetail";
import { makeStyles } from "@mui/styles";
import {useSnackbar} from "notistack";
import useMergeTrackedJobs from "./hooks/useMergeTrackedJobs";
import useScheduleJobAutoDelete from "./hooks/useScheduleJobAutoDelete";
import useJobPollingManager from "./hooks/useJobPollingManager";

export const DELETE_TIMEOUT_MS = 30000;
export const POLL_INTERVAL_MS = 5000;

const useStyles = makeStyles((theme) => ({
    startJobContainer: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'row'
    },
    jobsContainer: {
        display: 'flex',
        justifyContent: 'center',
    }
}))

/**
 * funzione helper per aggiornare now ogni secondo
 */
function useCountdownTimer() {
    const [now, setNow] = React.useState(Date.now());
    React.useEffect(() => {
        const timerId = setInterval(() => setNow(Date.now()), 1000);
        return () => { clearInterval(timerId) }
    }, []);
    return now;
}

function secondsFromNow(timestamp, now) {
    if (!timestamp) return undefined;
    return Math.floor((timestamp - now) / 1000);
}


export function NotificationJobs () {
    
    const classes = useStyles();
    const isAdmin = getIsAdmin(React.useContext(UserContext));
    const { t } = useTranslation();
    const { notificationJobs } = useGetAllNotificationJobs({ enabled: isAdmin });
    const queryClient = useQueryClient();
    const [error, setError] = React.useState();
    const { enqueueSnackbar } = useSnackbar();
    const [isStartingJob, setIsStartingJob] = React.useState(false);
    const activeJobPollers = React.useRef({});
    const deleteTimeouts = React.useRef({});
    const timersByJobId = React.useRef({});
    const now = useCountdownTimer();
    const trackedJobs = useMergeTrackedJobs({ notificationJobs, activeJobPollersRef: activeJobPollers });
    const deleteSchedulerCallback = useScheduleJobAutoDelete({ queryClient, deleteTimeoutsRef: deleteTimeouts, timersByJobId })

    useJobPollingManager({
        trackedJobs,
        activeJobPollersRef: activeJobPollers,
        timersByJobId,
        queryClient: queryClient,
        scheduleAutoDelete: deleteSchedulerCallback
    })

    const submitNotificationJob = async (messageValues) => {

        setIsStartingJob(false);
        const { status, data } = await post(`${MESSAGES}/notification-job`, { body: messageValues });   

        if(status === 202) {
            queryClient.invalidateQueries([MESSAGES, 'jobs', 'all']);
            enqueueSnackbar(t('notificationJob.jobSubmitted'), { variant: 'success' });
        }

        if(status === 400) {
            setError(data.key);
            enqueueSnackbar(t(`${error}`), { variant: 'error' });
        }

    }

    const handleManualDelete = async (jobId, setError) => {

        clearTimeout(deleteTimeouts.current[jobId]);
        delete deleteTimeouts.current[jobId];


        if(activeJobPollers.current[jobId]) {
            clearInterval(activeJobPollers.current[jobId].interval);
            delete activeJobPollers.current[jobId];
        }

        const { delError } = await deleteElem(`${MESSAGES}/jobs/${jobId}`)
            .then(({ status, data, error }) => ({ delError: error }));

        if (delError) {
            setError(delError);
        }

        queryClient.invalidateQueries([MESSAGES, 'jobs', 'all']); // refetch immediato

        delete timersByJobId.current[jobId];
    };

    return (
        <React.Fragment>
            <Box
            className={classes.startJobContainer}>
                <StartIconButton
                    onClick={() => setIsStartingJob(true)}
                    title={t('sendMessageToInitiatives')}
                    startIcon={<AddCircle />}
                    size={"large"}
                />
                <StartNotificationJobModal
                    open={isStartingJob}
                    onClose={() => setIsStartingJob(false)}
                    onSave={submitNotificationJob}
                />
            </Box>

            <Grid container spacing={1} className={classes.jobsContainer}>
                <Grid item xs={12}>
                    <Typography variant="h5" sx={{ mt: 2, textAlign: 'center' }}>
                        {t('inProgress')}
                    </Typography>
                    <Divider
                        orientation="horizontal"
                        sx={{ my: 3 }}
                    >
                    </Divider>
                </Grid>

                {
                    trackedJobs?.length > 0
                        ? (
                            trackedJobs.map(job => {
                                const jobDetails = timersByJobId.current[job.jobId];
                                return (
                                    <Grid key={job.jobId} item xs={12} sm={6} lg={4}>
                                        <NotificationJobCardDetail
                                            job={job}
                                            t={t}
                                            messageTitle={job.title}
                                            messageBody={job.body}
                                            onManualDelete={handleManualDelete}
                                            nextPollIn={secondsFromNow(jobDetails?.nextPollAt, now)}
                                            autoDeleteIn={secondsFromNow(jobDetails?.autoDeleteIn, now)}
                                        />
                                    </Grid>
                                )
                            })
                        )
                        : (
                            <Grid 
                                item xs={12}
                                sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Alert 
                                severity="info"
                            >
                                {t('notificationJob.noJobsToDisplay')}
                            </Alert>
                            </Grid>
                        )
                }
            </Grid>
        </React.Fragment>
    );
}